// The date bar sticks under the masthead (see sass/content/_daily-schedule.scss).
// The masthead's height changes with the breakpoint, the box office bar and
// wrapping, so it's measured rather than hard-coded.
function measureMasthead(daily: HTMLElement) {
  const masthead = document.getElementById("masthead");
  if (!masthead) return;

  const update = () => {
    const position = getComputedStyle(masthead).position;
    const sticks = position === "sticky" || position === "fixed";
    const height = sticks ? Math.round(masthead.getBoundingClientRect().height) : 0;
    daily.style.setProperty("--masthead-height", `${height}px`);
  };

  new ResizeObserver(update).observe(masthead);
  update();
}

// The page cache can serve the schedule a day after it was made (it carries
// that date as data-today): drop the days that have passed since, from the
// schedule, the chips and the calendar, and move Today to the real one.
// A ?today= preview (data-today-preview) keeps its own date.
function catchUpToToday() {
  const daily = document.querySelector<HTMLElement>(".program-daily");
  const made = daily?.dataset.today;
  if (!daily || !made || daily.hasAttribute("data-today-preview")) return;
  // en-CA writes dates as YYYY-MM-DD.
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Vancouver" }).format(new Date());
  if (today <= made) return;
  daily.dataset.today = today;

  const weekday = (date: string, length: "short" | "long") =>
    new Date(`${date}T12:00:00`).toLocaleDateString("en-US", { weekday: length });
  const marked = (date: string) => document.querySelectorAll<HTMLElement>(`[data-date="${date}"]`);

  document.querySelectorAll<HTMLElement>(".program-daily-day[data-date], .program-filters-chip[data-date]").forEach((node) => {
    if (node.dataset.date! < today) node.remove();
  });
  document.querySelectorAll<HTMLElement>(".program-calendar-cell[data-date]").forEach((cell) => {
    const link = cell.querySelector("a");
    if (cell.dataset.date! >= today || !link) return;
    const date = cell.dataset.date!;
    const day = document.createElement("span");
    const hidden = document.createElement("span");
    hidden.className = "visually-hidden";
    hidden.textContent = `, ${weekday(date, "long")}, ${new Date(`${date}T12:00:00`).toLocaleDateString("en-US", { month: "long" })} (past)`;
    day.append(link.textContent!.trim(), hidden);
    link.replaceWith(day);
    cell.classList.add("is-past");
    if (cell.dataset.label) cell.dataset.label = cell.dataset.label.replace(/^Today,/, `${weekday(date, "short")},`);
  });

  // Today: its tag in the day's heading, the chip's label and aria-current.
  document.querySelectorAll(".program-daily-today").forEach((tag) => {
    const tags = tag.parentElement;
    tag.remove();
    if (tags && !tags.children.length) tags.remove();
  });
  document.querySelectorAll<HTMLAnchorElement>('.program-daily a[aria-current="date"]').forEach((link) => {
    link.removeAttribute("aria-current");
    link.setAttribute("aria-label", (link.getAttribute("aria-label") || "").replace(/^Today, /, ""));
    const chip = link.closest<HTMLElement>(".program-filters-chip");
    const label = chip?.querySelector("strong");
    if (label && chip?.dataset.date) label.textContent = weekday(chip.dataset.date, "short");
  });
  marked(today).forEach((node) => {
    // The calendar's label for the phone bar ("Today, Jan 23").
    if (node.dataset.label) node.dataset.label = node.dataset.label.replace(/^\w+,/, "Today,");
    const link = node.matches(".program-daily-day") ? null : node.querySelector("a");
    if (link) {
      link.setAttribute("aria-current", "date");
      link.setAttribute("aria-label", `Today, ${link.getAttribute("aria-label") || ""}`);
      const label = link.querySelector("strong");
      if (label) label.textContent = "Today";
      return;
    }
    const heading = node.querySelector(".program-daily-heading");
    if (!heading) return;
    let tags = heading.querySelector(".program-daily-tags");
    if (!tags) {
      tags = document.createElement("p");
      tags.className = "program-daily-tags";
      heading.querySelector("h3")?.after(tags);
    }
    const tag = document.createElement("span");
    tag.className = "program-daily-today";
    tag.textContent = "Today";
    tags.prepend(tag);
  });

  // The phones' Jump to day names the first day left.
  const first = document.querySelector<HTMLElement>(".program-calendar-cell[data-date]:not(.is-past)");
  const toggleDay = document.querySelector(".program-calendar-toggle-day");
  if (first?.dataset.label && toggleDay) toggleDay.textContent = first.dataset.label;
}

function initProgramDatesScroll() {
  const daily = document.querySelector<HTMLElement>(".program-daily");
  const calendar = document.querySelector<HTMLElement>(".program-calendar");
  const toggle = calendar?.querySelector<HTMLButtonElement>(".program-calendar-header button");
  const toggleDay = toggle?.querySelector<HTMLElement>(".program-calendar-toggle-day");
  const calendarContent = calendar?.querySelector<HTMLElement>(".program-calendar-content");
  const calendarPanel = document.getElementById("program-calendar-panel");
  const nav = document.querySelector<HTMLElement>(".program-dates");
  const scroller = nav?.querySelector<HTMLElement>(".program-dates-wrapper");
  const prev = nav?.querySelector<HTMLButtonElement>(".program-dates-scroll--prev");
  const next = nav?.querySelector<HTMLButtonElement>(".program-dates-scroll--next");
  const days = document.querySelectorAll<HTMLElement>(".program-daily-day");
  const chipMq = window.matchMedia("(min-width: 480px)");

  if (!nav && !calendar) return;

  if (daily) {
    measureMasthead(daily);
  }

  let ignoreSpyUntil = 0;
  let currentDayId = "";
  let closeTimer: number | null = null;
  let spyFrame = 0;

  const pauseSpy = (ms = 800) => {
    ignoreSpyUntil = Date.now() + ms;
  };

  const chipsVisible = () => chipMq.matches;

  // Where a day's heading sticks: under the admin bar, masthead and date bar.
  const getStickyOffset = () => {
    if (!daily) return 0;
    const probe = daily.querySelector<HTMLElement>(".program-daily-heading");
    return probe ? parseFloat(getComputedStyle(probe).top) || 0 : 0;
  };

  const getDateBarBottom = () => {
    const bar = chipsVisible() ? nav : calendar;
    if (bar && getComputedStyle(bar).display !== "none") {
      return bar.getBoundingClientRect().bottom;
    }
    return getStickyOffset();
  };

  const dayMarker = (day: HTMLElement) =>
    day.querySelector<HTMLElement>(".program-daily-heading") ?? day;

  // The phone calendar's panel drops down over the page, so the bar's height
  // is the toggle's either way.
  const measureDatepicker = () => {
    if (!daily) return;
    const el = chipsVisible() ? nav : calendar;
    const height =
      el && getComputedStyle(el).display !== "none" ? el.getBoundingClientRect().height : 0;
    if (height > 0) {
      daily.style.setProperty("--date-bar-height", `${Math.round(height)}px`);
    }
  };

  const setCurrentDayById = (id: string) => {
    if (!id) return;
    currentDayId = id;

    document
      .querySelectorAll(".program-calendar-cell.is-current, .program-filters-chip.is-current")
      .forEach((el) => el.classList.remove("is-current"));

    const cell = calendar?.querySelector<HTMLElement>(
      `.program-calendar-cell[data-day="${CSS.escape(id)}"]`
    );
    cell?.classList.add("is-current");

    // The phone bar names the day in view.
    if (toggleDay && cell?.dataset.label) {
      toggleDay.textContent = cell.dataset.label;
    }

    const chip = nav?.querySelector<HTMLElement>(
      `.program-filters-chip[data-day="${CSS.escape(id)}"]`
    );
    chip?.classList.add("is-current");

    if (!nav || !scroller || !chipsVisible() || !chip) return;

    // The strip tracks the days: each one scrolls it the same share of its
    // overflow, so it sits at the start on the first day (the track's
    // padding lining the chips up with the page's column) and at the end on
    // the last, and the current chip drifts across as the days go by. It
    // never stops under the 4rem fades behind the scroll buttons: a chip
    // there pulls the strip a chip's gap clear of them.
    const chips = Array.from(nav.querySelectorAll<HTMLElement>(".program-filters-chip"));
    const index = chips.indexOf(chip);
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const clearance = 4.5 * rem;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    if (maxScroll <= 0 || index < 0) return;
    const chipOffset =
      chip.getBoundingClientRect().left -
      scroller.getBoundingClientRect().left +
      scroller.scrollLeft;
    const chipRight = chipOffset + chip.offsetWidth;

    let left = chips.length > 1 ? (maxScroll * index) / (chips.length - 1) : 0;
    if (index > 0) left = Math.min(left, chipOffset - clearance);
    if (index < chips.length - 1) left = Math.max(left, chipRight - scroller.clientWidth + clearance);
    left = Math.min(maxScroll, Math.max(0, left));
    if (Math.abs(left - scroller.scrollLeft) > 1) {
      scroller.scrollTo({
        left,
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      });
    }
  };

  const setCurrentDay = (day: HTMLElement) => {
    if (Date.now() < ignoreSpyUntil) return;
    setCurrentDayById(day.id);
  };

  const pickCurrentDay = () => {
    const panel = document.getElementById("daily-panel");
    if (panel?.hidden || days.length === 0) return;
    if (Date.now() < ignoreSpyUntil) return;

    // Offset for scroll-margin-top
    const line = getDateBarBottom() + 64;
    let next: HTMLElement = days[0];
    days.forEach((day) => {
      if (dayMarker(day).getBoundingClientRect().top <= line) {
        next = day;
      }
    });

    const active = currentDayId ? document.getElementById(currentDayId) : null;
    if (active && active !== next) {
      const activeIndex = Array.from(days).indexOf(active);
      const nextIndex = Array.from(days).indexOf(next);
      if (
        activeIndex >= 0 &&
        nextIndex < activeIndex &&
        dayMarker(active).getBoundingClientRect().top <= line
      ) {
        return;
      }
    }

    setCurrentDay(next);
  };

  const schedulePickCurrentDay = () => {
    if (spyFrame) return;
    spyFrame = requestAnimationFrame(() => {
      spyFrame = 0;
      pickCurrentDay();
    });
  };

  const updateButtons = () => {
    if (!scroller || !prev || !next) return;
    if (!chipsVisible()) {
      prev.hidden = true;
      next.hidden = true;
      return;
    }

    const track = scroller.querySelector<HTMLElement>(".program-dates-track");
    const list = scroller.querySelector<HTMLElement>(".program-filters-chips");
    if (!track || !list) {
      prev.hidden = true;
      next.hidden = true;
      return;
    }

    // Right padding is only for aligning the last chip when scrolled; it should
    // not count as overflow when every chip is already visible.
    const leftPad = parseFloat(getComputedStyle(track).paddingLeft) || 0;
    const fitsWithoutScroll = list.scrollWidth + leftPad <= scroller.clientWidth + 1;

    if (fitsWithoutScroll) {
      prev.hidden = true;
      next.hidden = true;
      return;
    }

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const atStart = scroller.scrollLeft <= 1;
    const atEnd = scroller.scrollLeft >= maxScroll - 1;

    prev.hidden = atStart;
    next.hidden = atEnd;
  };

  const getScrollAmount = () => {
    if (!scroller) return 0;
    const chip = scroller.querySelector<HTMLElement>(".program-filters-chip");
    const list = scroller.querySelector<HTMLElement>(".program-filters-chips");
    if (!chip || !list) return scroller.clientWidth * 0.8;
    const gap = parseFloat(getComputedStyle(list).columnGap || getComputedStyle(list).gap) || 0;
    return (chip.offsetWidth + gap) * 3;
  };

  const scrollByChips = (direction: number) => {
    if (!scroller) return;
    pauseSpy();
    scroller.scrollBy({
      left: direction * getScrollAmount(),
      behavior: "smooth",
    });
  };

  const finishCalendarTransition = () => {
    if (closeTimer !== null) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }
    if (toggle?.getAttribute("aria-expanded") === "false" && calendarPanel) {
      calendarPanel.hidden = true;
    }
    measureDatepicker();
    pickCurrentDay();
  };

  const setCalendarExpanded = (expanded: boolean) => {
    if (!toggle || !calendarPanel) return;

    if (closeTimer !== null) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }

    if (expanded) {
      calendarPanel.hidden = false;
      requestAnimationFrame(() => {
        toggle.setAttribute("aria-expanded", "true");
      });
      return;
    }

    toggle.setAttribute("aria-expanded", "false");
    closeTimer = window.setTimeout(finishCalendarTransition, 400);
  };

  const calendarOpen = () => toggle?.getAttribute("aria-expanded") === "true";

  if (calendar && toggle && calendarPanel) {
    toggle.addEventListener("click", () => {
      setCalendarExpanded(!calendarOpen());
    });

    // It's a drop-down over the schedule: Escape or a click elsewhere closes it.
    calendar.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !calendarOpen()) return;
      setCalendarExpanded(false);
      toggle.focus();
    });

    document.addEventListener("click", (event) => {
      if (!calendarOpen() || calendar.contains(event.target as Node)) return;
      setCalendarExpanded(false);
    });
  }

  calendarContent?.addEventListener("transitionend", (event) => {
    if (event.propertyName !== "grid-template-rows") return;
    finishCalendarTransition();
  });

  const jumpToDay = (id: string) => {
    const day = document.getElementById(id);
    if (!day) return;

    setCurrentDayById(id);
    measureDatepicker();

    // Smooth-scrolling takes longer than a fixed pause can predict, so keep
    // the spy suppressed until the scroll actually settles (with a timeout
    // fallback for browsers without the `scrollend` event).
    pauseSpy(2000);
    let scrollSettled = false;
    const resumeSpy = () => {
      if (scrollSettled) return;
      scrollSettled = true;
      window.removeEventListener("scrollend", resumeSpy);
      ignoreSpyUntil = 0;
    };
    window.addEventListener("scrollend", resumeSpy, { once: true });
    window.setTimeout(resumeSpy, 1500);

    day.scrollIntoView({ behavior: "smooth", block: "start" });

    if (`#${id}` !== window.location.hash) {
      history.pushState(null, "", `#${id}`);
    }
    day.tabIndex = -1;
    day.focus({ preventScroll: true });
  };

  const onDayLinkClick = (event: MouseEvent) => {
    const link = event.currentTarget as HTMLAnchorElement;
    const id = decodeURIComponent(link.hash.replace(/^#/, ""));
    if (!document.getElementById(id)) return;
    event.preventDefault();
    if (calendar?.contains(link) && calendarOpen()) {
      setCalendarExpanded(false);
    }
    jumpToDay(id);
  };

  if (nav && scroller && prev && next) {
    prev.addEventListener("click", () => scrollByChips(-1));
    next.addEventListener("click", () => scrollByChips(1));
    scroller.addEventListener("scroll", updateButtons, { passive: true });
    scroller.addEventListener("pointerdown", () => pauseSpy(1200));
    scroller.addEventListener("wheel", () => pauseSpy(1200), { passive: true });
    nav.querySelectorAll<HTMLAnchorElement>("a[href^='#']").forEach((link) => {
      link.addEventListener("click", onDayLinkClick);
    });
    new ResizeObserver(() => {
      updateButtons();
      measureDatepicker();
    }).observe(scroller);
    const track = scroller.querySelector(".program-dates-track");
    if (track) {
      new ResizeObserver(updateButtons).observe(track);
    }
    updateButtons();
  }

  calendar?.querySelectorAll<HTMLAnchorElement>("a[href^='#']").forEach((link) => {
    link.addEventListener("click", onDayLinkClick);
  });

  if (calendar) {
    new ResizeObserver(measureDatepicker).observe(calendar);
  }

  chipMq.addEventListener("change", () => {
    if (chipsVisible() && calendarOpen()) {
      setCalendarExpanded(false);
    }
    updateButtons();
    measureDatepicker();
    pickCurrentDay();
  });
  window.addEventListener("scroll", schedulePickCurrentDay, { passive: true });
  measureDatepicker();
  pickCurrentDay();
}

function initDailyShowtimesExpand() {
  document.querySelector(".program-daily")?.addEventListener("click", (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
      "button.program-daily-more"
    );
    if (!button) return;

    const listId = button.getAttribute("aria-controls");
    const list = listId ? document.getElementById(listId) : button.previousElementSibling;
    if (!(list instanceof HTMLUListElement)) return;

    const extras = list.querySelectorAll<HTMLElement>(".program-daily-showtime-extra");
    extras.forEach((item) => {
      item.hidden = false;
    });

    const firstExtra = extras[0];
    if (firstExtra) {
      firstExtra.tabIndex = -1;
    }
    button.remove();
    firstExtra?.focus();
  });
}

catchUpToToday();
initProgramDatesScroll();
initDailyShowtimesExpand();
